/**
 * MatterTimeCard — "log time / see time logged" on a matter's own page
 * (time-recording-foundations, Ticket 5). The "wherever they are" entry
 * point: a fee earner never has to leave the matter to log time against it.
 */
import { useState } from "react";
import { Clock, Plus, X } from "lucide-react";
import { Button } from "./ui/button";
import TimeEntryForm from "./TimeEntryForm";
import TimeEntryList from "./TimeEntryList";
import TimeClockList from "./TimeClockList";
import LawBillityImportPanel from "./LawBillityImportPanel";

// embedded: drawn inside the matter overview card (money bar, invoices),
// so it carries no card of its own (Vanessa, 27 Oct 2026).
// actions: more buttons beside "Log time" (the matter page puts Invoice request here).
export default function MatterTimeCard({ matterRef, embedded = false, actions = null }) {
  const [showForm, setShowForm] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleSaved = () => {
    setShowForm(false);
    setRefreshKey((k) => k + 1);
  };

  return (
    <section
      className={embedded ? "" : "mt-6 bg-white/[0.02] border border-white/10 rounded-lg p-5"}
      data-testid="matter-time-card"
    >
      {/* 4 Nov 2026 tidy: one row of actions (From LawBillity, Invoice request, Log time) beside the heading. */}
      <div className={`flex flex-wrap items-center gap-2 ${embedded ? "mb-3" : "mb-4"}`}>
        <h2 className="mr-auto text-base font-semibold text-white flex items-center gap-2">
          <Clock className="w-4 h-4 text-indigo-300" />
          Time
        </h2>
        {/* BM-H4392: directors, accounts and the matter's fee earner bring unbilled LawBillity time across. */}
        <LawBillityImportPanel matterRef={matterRef} inline onImported={() => setRefreshKey((k) => k + 1)} />
        {actions}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowForm((s) => !s)}
          data-testid="matter-time-log-btn"
        >
          {showForm ? <X className="w-3.5 h-3.5 mr-1" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
          {showForm ? "Cancel" : "Log time"}
        </Button>
      </div>

      {showForm && (
        <div className="mb-4">
          <TimeEntryForm matterRef={matterRef} onSaved={handleSaved} onCancel={() => setShowForm(false)} />
        </div>
      )}

      {/* BM-H4296 (Vanessa, 13 Oct 2026): the same clock as My Time, fixed to this matter.
          Vanessa, 4 Nov 2026: "just remove time recording ... we have start a clock everywhere".
          Embedded on the matter page, the card no longer carries its own clock; the clock in
          the top bar starts and finishes clocks for any matter. */}
      {!embedded && (
        <div className="mb-3" data-testid="matter-time-clock">
          <h3 className="text-xs font-semibold text-slate-300 mb-2">Clock</h3>
          <TimeClockList fixedMatterRef={matterRef} onFinished={() => setRefreshKey((k) => k + 1)} />
        </div>
      )}

      <div className={embedded ? "max-h-[18rem] overflow-auto" : ""} data-testid="matter-time-entries">
        <TimeEntryList key={refreshKey} matterRef={matterRef} showMatterColumn={false} showFeeEarner compactEmpty />
      </div>
    </section>
  );
}
